// Client POV — facility edit page used by logged-in client users
/**
 * EditFacilityPage
 *
 * Thin page-level wrapper for the edit facility flow.
 * Connects the useEditFacility hook to the EditFacilityForm component.
 * Route access guard (role check) is handled by EditFacilityPageWrapper
 * in src/app/routes/index.tsx.
 *
 * For CLIENT-role users, useUserPrivileges fetches AVP permissions on every
 * mount (including page refreshes). If canEditFacility is false, the access
 * denied screen is shown immediately — before the form ever renders.
 * isLoading is held true until both the facility data and privileges have
 * resolved, preventing a form flash before the access denied screen appears.
 */
import React from 'react';

import { useUserPrivileges } from '@/shared/hooks/useUserPrivileges';
import { useUserRole } from '@/shared/hooks/useUserRole';
import { useClientId } from '@/shared/hooks/useClientId';
import { useClientStatus } from '@/shared/hooks/useClientStatus';
import { FACILITY_LABELS } from '../../constants/facilityLabels';
import { EditFacilityForm } from '../../components/EditFacilityForm';
import { useEditFacility } from '../../hooks/useEditFacility';

interface EditFacilityPageProps {
  facilityId: string;
}

const EditFacilityPage: React.FC<EditFacilityPageProps> = ({ facilityId }) => {
  const hook = useEditFacility(facilityId);
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();
  const { isClient } = useUserRole();
  const { clientId } = useClientId();
  const { clientStatus, isLoading: clientStatusLoading } = useClientStatus();

  const isAccessDenied =
    hook.isAccessDenied ||
    (!privilegesLoading && isClient && !privileges.canEditFacility);
  const isLoading =
    hook.isLoading || privilegesLoading || (isClient && clientStatusLoading);

  const canListFacilityUser = privilegesLoading
    ? false
    : privileges.canListFacilityUser;
  const canCreateFacilityUser = privilegesLoading
    ? true
    : privileges.canCreateFacilityUser;
  const canEditFacilityUser = privilegesLoading
    ? false
    : privileges.canEditFacilityUser;
  const canCreateEquipment = privilegesLoading
    ? true
    : privileges.canCreateEquipment;
  const canEditEquipment = privilegesLoading
    ? false
    : privileges.canEditEquipment;
  const canListEquipment = privilegesLoading
    ? false
    : privileges.canListEquipment;
  const canToggleEmployeeStatus = privilegesLoading
    ? false
    : privileges.canToggleEmployeeStatus;

  const facilityStatus = hook.facilityStatus;
  let isToggleDisabled = false;
  let toggleDisabledReason: string | undefined;

  if (facilityStatus && facilityStatus !== 'active') {
    isToggleDisabled = true;
    toggleDisabledReason = FACILITY_LABELS.TOGGLE_EMPLOYEE_DISABLED_FACILITY;
  } else if (isClient && clientStatus && clientStatus !== 'active') {
    isToggleDisabled = true;
    toggleDisabledReason = FACILITY_LABELS.TOGGLE_EMPLOYEE_DISABLED_CLIENT;
  }

  return (
    <EditFacilityForm
      facilityId={facilityId}
      {...hook}
      clientId={isClient ? clientId : null}
      isLoading={isLoading}
      isAccessDenied={isAccessDenied}
      canListFacilityUser={canListFacilityUser}
      canCreateFacilityUser={canCreateFacilityUser}
      canEditFacilityUser={canEditFacilityUser}
      canCreateEquipment={canCreateEquipment}
      canEditEquipment={canEditEquipment}
      canListEquipment={canListEquipment}
      canToggleEmployeeStatus={canToggleEmployeeStatus}
      isToggleDisabled={isToggleDisabled}
      toggleDisabledReason={toggleDisabledReason}
    />
  );
};

export default EditFacilityPage;
